import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { fetchTemplates, deleteTemplate } from '../store/slices/templatesSlice';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

function Templates() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { templates, loading, error } = useSelector((state) => state.templates);
  const { userInfo } = useSelector((state) => state.auth);
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const [defaultTemplateId, setDefaultTemplateId] = React.useState(null);
  const isAdmin = activeCompany?.role === 'admin' || userInfo?.is_superadmin;

  useEffect(() => {
    // Always fetch fresh templates when component mounts
    dispatch(fetchTemplates());
    fetchDefaultTemplate();
  }, [dispatch]);

  const fetchDefaultTemplate = async () => {
    try {
      const response = await api.get('/api/settings/default_template_id');
      if (response.data && response.data.value) {
        setDefaultTemplateId(response.data.value);
      }
    } catch (_err) {
      // Setting might not exist yet
      // Setting might not exist yet - this is expected behavior
    }
  };

  const setDefaultTemplate = async (templateId) => {
    try {
      await api.put('/api/settings/default_template_id', { 
        value: templateId === defaultTemplateId ? null : templateId 
      });
      setDefaultTemplateId(templateId === defaultTemplateId ? null : templateId);
    } catch (_err) {
      // Error setting default template - fail silently as this is a non-critical feature
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      await dispatch(deleteTemplate(id));
      // Refresh templates after deletion
      dispatch(fetchTemplates());
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Box>
      <PageHeader
        title="Watermark Templates"
        subtitle="Click the star to set the default template for new jobs"
        actions={
          isAdmin ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/templates/create')}>Create Template</Button>
          ) : undefined
        }
      />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading templates: {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Default template for new jobs">
                    <span>Default</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '25%' }}>
                  <Tooltip title="Name of the watermark template">
                    <span>Template Name</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Static or moving watermark">
                    <span>Type</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  <Tooltip title="Font family and size">
                    <span>Font</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Text color">
                    <span>Color</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Watermark transparency level">
                    <span>Opacity</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>
                  <Tooltip title="Number of watermark positions">
                    <span>Layers</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  <Tooltip title="Date template was created">
                    <span>Created</span>
                  </Tooltip>
                </TableCell>
                {isAdmin && (
                  <TableCell align="center" sx={{ width: '10%' }}>
                    <Tooltip title="Available actions">
                      <span>Actions</span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell 
                    colSpan={isAdmin ? 9 : 8} 
                    align="center"
                    sx={{ height: 300 }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={isAdmin ? 9 : 8} 
                    align="center"
                    sx={{ height: 300 }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%'
                      }}
                    >
                      <Typography color="text.secondary">
                        {isAdmin ? 'No templates yet. Click "Create Template" to add one.' : 'No templates have been created yet.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      {isAdmin ? (
                        <Tooltip title={defaultTemplateId === template.id ? "Remove as default" : "Set as default for new jobs"}>
                          <IconButton
                            size="small"
                            onClick={() => setDefaultTemplate(template.id)}
                            color={defaultTemplateId === template.id ? 'primary' : 'default'}
                          >
                            {defaultTemplateId === template.id ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        defaultTemplateId === template.id && <StarIcon color="primary" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{template.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={template.position_type === 'moving' ? 'Watermark moves to different positions' : 'Watermark stays in fixed positions'} arrow placement="top">
                        <Chip
                          label={template.position_type || 'static'}
                          size="small"
                          color={template.position_type === 'moving' ? 'primary' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.font_name} {template.font_size}pt
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Watermark text color" arrow placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: template.font_color || '#FFFFFF',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {template.font_color}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {Math.round((template.opacity || 0.5) * 100)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {template.positions?.length || 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(template.created_at)}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {/* Universal templates (company_id NULL) are
                              shared across every workspace. End users
                              can't edit / delete — only Duplicate, which
                              creates a workspace-local copy they own. */}
                          {template.company_id == null ? (
                            <>
                              <Tooltip title="Universal template — shared across all workspaces. Duplicate to make your own version." arrow placement="top">
                                <Chip size="small" label="Universal" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                              </Tooltip>
                              <Tooltip title="Duplicate to your workspace (you can then edit your copy)" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      const { data } = await api.post(`/api/templates/${template.id}/duplicate`);
                                      dispatch(fetchTemplates());
                                      navigate(`/templates/edit/${data.id}`);
                                    } catch (e) {
                                      alert(`Duplicate failed: ${e?.response?.data?.detail || e.message || 'unknown error'}`);
                                    }
                                  }}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              {/* v10.21.9: HHMG operators can delete universal
                                  templates (shared across every workspace).
                                  Non-operator admins are still locked out so
                                  one company can't unilaterally remove a
                                  template another company depends on. */}
                              {userInfo?.is_superadmin && (
                                <Tooltip title="Operator-only: delete this universal template for every workspace" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(template.id)}
                                    sx={{
                                      color: 'error.main',
                                      '&:hover': { backgroundColor: 'error.lighter' },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit watermark template design" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/templates/edit/${template.id}`)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'action.hover' }
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Duplicate this template" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      const { data } = await api.post(`/api/templates/${template.id}/duplicate`);
                                      dispatch(fetchTemplates());
                                      navigate(`/templates/edit/${data.id}`);
                                    } catch (e) {
                                      alert(`Duplicate failed: ${e?.response?.data?.detail || e.message || 'unknown error'}`);
                                    }
                                  }}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently delete this template" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(template.id)}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': { backgroundColor: 'error.lighter' }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default Templates;